// =============================================================================
// supplier-dashboard-client.http.ts — Adaptateur HTTP RÉEL du SupplierDashboardClient (Lot A / D3b).
// =============================================================================
// Câble la SPA contre l'endpoint FOURNISSEUR `GET /supplier/dashboard` (DBU §3.2). Substitué derrière le
// MÊME DI seam (SupplierDashboardClientProvider). LECTURE PURE : aucune mutation, donc PAS de CSRF.
//
// SÉCURITÉ (RULE_REF_006) : la session fournisseur (cookie httpOnly `supplier_session`) n'est JAMAIS lue
// ni fabriquée ici — portée par le cookie (credentials: 'include'). Le siret vient de la session côté API.
// AUCUNE donnée métier en localStorage. MAPPING ERREURS par STATUS HTTP (401/403/404), message relayé.

import {
  SupplierDashboardApiError,
  type SupplierDashboardApiErrorKind,
  type SupplierDashboardClient,
  type SupplierDashboardDto,
} from '@/api/supplier-dashboard-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Mapping STATUS HTTP → kind UX (lecture : 401 session, 403 CSRF/garde, 404 ressource). */
const STATUS_TO_KIND: Readonly<Record<number, SupplierDashboardApiErrorKind>> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
};

/**
 * Adaptateur HTTP réel : appel `fetch` contre l'API dashboard fournisseur. `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/supplier/*`).
 */
export class HttpSupplierDashboardClient implements SupplierDashboardClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async getDashboard(): Promise<SupplierDashboardDto> {
    const response = await fetch(`${this.baseUrl}/supplier/dashboard`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierDashboardDto;
  }

  private async toApiError(response: Response): Promise<SupplierDashboardApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new SupplierDashboardApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
